import { useEffect, useState, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  getCachedMessages, fetchRecentMessages, fetchMessagesByDateRange,
  fullReloadMessages, deleteMessage, deleteMultipleMessages,
  deleteAllMessages, deleteAllBotMessages, sendMessage, getCacheTimestamp,
} from '../services/messageService';
import { findUserByNickname, findUserByEmail, searchUsersByNickname } from '../services/userService';
import UserFinder from '../components/common/UserFinder';
import { useAuthStore } from '../stores/authStore';
import { formatTimestamp } from '../utils/time';
import { coordsToFrequency, frequencyToCoords, calcDistance, formatDistance } from '../utils/frequency';
import { downloadCSV, downloadJSON } from '../utils/export';
import MessageDetailModal from '../components/common/MessageDetailModal';
import UserDetailModal from '../components/common/UserDetailModal';
import type { PublicMessage, User } from '../types';

type FilterMode = 'all' | 'users' | 'bots';

const PER_PAGE_OPTIONS = [10, 25, 50, 100, 150, 200, 300, 400, 500];
const LS_PREFIX = 'admin_msg_';

function ls(key: string, fallback: string): string {
  return localStorage.getItem(LS_PREFIX + key) ?? fallback;
}
function lsSet(key: string, val: string) {
  localStorage.setItem(LS_PREFIX + key, val);
}
function getStoredPerPage(): number {
  const v = parseInt(ls('perPage', '50'));
  return PER_PAGE_OPTIONS.includes(v) ? v : 50;
}

function generateRandomNearby(lat: number, lng: number, minM: number, maxM: number): { lat: number; lng: number } {
  const dist = minM + Math.random() * (maxM - minM);
  const angle = Math.random() * 2 * Math.PI;
  const dLat = (dist * Math.cos(angle)) / 111320;
  const dLng = (dist * Math.sin(angle)) / (111320 * Math.cos(lat * Math.PI / 180));
  return { lat: lat + dLat, lng: lng + dLng };
}

export default function MessagesPage() {
  const { adminUser } = useAuthStore();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view'); // 'today' | 'all' | null
  const [allMessages, setAllMessages] = useState<PublicMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState<FilterMode>(() => (ls('filterMode', 'all') as FilterMode));
  const [excludeBots, setExcludeBots] = useState(() => ls('excludeBots', 'true') === 'true');
  const [excludeNotifications, setExcludeNotifications] = useState(() => ls('excludeNotifications', 'true') === 'true');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [perPage, setPerPage] = useState(getStoredPerPage);
  const [currentPage, setCurrentPage] = useState(1);

  // Auto-refresh (realtime polling)
  const [autoRefresh, setAutoRefresh] = useState(false);
  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tableEndRef = useRef<HTMLTableRowElement>(null);
  const prevMsgCountRef = useRef(0);

  // Virtual sender (persisted)
  const [senderEmail, setSenderEmail] = useState(() => ls('senderEmail', ''));
  const [senderNickname, setSenderNickname] = useState(() => ls('senderNickname', ''));
  const [senderGender, setSenderGender] = useState<'male' | 'female' | 'unknown'>(() => ls('senderGender', 'female') as 'male' | 'female' | 'unknown');
  const [senderLat, setSenderLat] = useState(() => ls('senderLat', ''));
  const [senderLng, setSenderLng] = useState(() => ls('senderLng', ''));
  const [senderId, setSenderId] = useState(() => ls('senderId', ''));
  const [appliedDistance, setAppliedDistance] = useState('');
  const [sendText, setSendText] = useState('');

  // Nickname search
  const [nicknameResults, setNicknameResults] = useState<User[]>([]);
  const [showNicknameDropdown, setShowNicknameDropdown] = useState(false);

  // Frequency calculator
  const [freq1, setFreq1] = useState('');
  const [freq2, setFreq2] = useState('');
  const [freqDistance, setFreqDistance] = useState('');

  // Random location (persisted)
  const [minDistance, setMinDistance] = useState(() => ls('minDistance', '1500'));
  const [maxDistance, setMaxDistance] = useState(() => ls('maxDistance', '5000'));

  // Date range fetch
  const [showDateRange, setShowDateRange] = useState(false);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');

  // Modals
  const [detailMsg, setDetailMsg] = useState<PublicMessage | null>(null);
  const [showUserModal, setShowUserModal] = useState<string | null>(null);
  const [selectedMsg, setSelectedMsg] = useState<PublicMessage | null>(null);

  // ─── Init: load from cache, fetch recent, then jump to latest ───
  const initDone = useRef(false);
  // When true, background poll won't overwrite the current list
  const pollPausedRef = useRef(viewParam === 'today' || viewParam === 'all');
  useEffect(() => {
    if (viewParam === 'today') {
      pollPausedRef.current = true;
      setLoading(true);
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      fetchMessagesByDateRange(start, end).then(msgs => {
        setAllMessages(msgs);
        initDone.current = true;
        setLoading(false);
        setSearchParams({}, { replace: true });
      });
      return;
    }
    if (viewParam === 'all') {
      pollPausedRef.current = true;
      setLoading(true);
      fullReloadMessages(10000).then(msgs => {
        setAllMessages(msgs);
        initDone.current = true;
        setLoading(false);
        setSearchParams({}, { replace: true });
      });
      return;
    }
    const cached = getCachedMessages();
    if (cached.length > 0) setAllMessages(cached);
    handleFetchRecent().then(() => {
      if (!initDone.current) {
        initDone.current = true;
        goToLatest();
      }
    });
  }, []);

  // ─── Jump to last page & scroll bottom ───
  const autoRefreshStateRef = useRef(autoRefresh);
  useEffect(() => { autoRefreshStateRef.current = autoRefresh; }, [autoRefresh]);

  const totalPagesRef = useRef(1);

  function goToLatest() {
    setCurrentPage(totalPagesRef.current);
    setTimeout(() => tableEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 200);
  }

  // ─── Background polling (always runs, goToLatest only when autoRefresh ON) ───
  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function poll() {
      if (!active) return;
      // Skip poll when user is viewing a filtered/date-range result
      if (pollPausedRef.current) {
        if (active) timer = setTimeout(poll, 5000);
        return;
      }
      try {
        const prevCount = prevMsgCountRef.current;
        const msgs = await fetchRecentMessages(500);
        if (!active) return;
        setAllMessages(msgs);
        // Only auto-scroll when "실시간" is checked
        if (msgs.length > prevCount && autoRefreshStateRef.current) {
          goToLatest();
        }
        prevMsgCountRef.current = msgs.length;
      } catch { /* ignore */ }
      if (active) {
        timer = setTimeout(poll, 5000);
      }
    }

    poll();

    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Track message count
  useEffect(() => { prevMsgCountRef.current = allMessages.length; }, [allMessages]);

  // ─── Per page persistence ───
  function handlePerPageChange(val: number) {
    setPerPage(val);
    setCurrentPage(1);
    lsSet('perPage', String(val));
  }

  // ─── Persist settings to localStorage ───
  useEffect(() => { lsSet('filterMode', filterMode); }, [filterMode]);
  useEffect(() => { lsSet('excludeBots', String(excludeBots)); }, [excludeBots]);
  useEffect(() => { lsSet('excludeNotifications', String(excludeNotifications)); }, [excludeNotifications]);
  useEffect(() => { lsSet('senderEmail', senderEmail); }, [senderEmail]);
  useEffect(() => { lsSet('senderNickname', senderNickname); }, [senderNickname]);
  useEffect(() => { lsSet('senderGender', senderGender); }, [senderGender]);
  useEffect(() => { lsSet('senderLat', senderLat); }, [senderLat]);
  useEffect(() => { lsSet('senderLng', senderLng); }, [senderLng]);
  useEffect(() => { lsSet('senderId', senderId); }, [senderId]);
  useEffect(() => { lsSet('minDistance', minDistance); }, [minDistance]);
  useEffect(() => { lsSet('maxDistance', maxDistance); }, [maxDistance]);

  // ─── Fetch handlers ───
  async function handleFetchRecent() {
    pollPausedRef.current = false;
    setLoading(true);
    try {
      const msgs = await fetchRecentMessages(2000);
      setAllMessages(msgs);
    } finally { setLoading(false); }
  }

  async function handleFetchPeriod(days: number) {
    pollPausedRef.current = true;
    setLoading(true);
    try {
      const end = new Date();
      const start = new Date(Date.now() - days * 86400000);
      const msgs = await fetchMessagesByDateRange(start, end);
      setAllMessages(msgs);
    } finally { setLoading(false); }
  }

  async function handleFetchCustomRange() {
    if (!dateStart || !dateEnd) return;
    pollPausedRef.current = true;
    setLoading(true);
    try {
      const start = new Date(dateStart);
      const end = new Date(dateEnd + 'T23:59:59');
      const msgs = await fetchMessagesByDateRange(start, end);
      setAllMessages(msgs);
      setShowDateRange(false);
    } finally { setLoading(false); }
  }

  async function handleFullReload() {
    if (!confirm('캐시를 삭제하고 서버에서 전체 다시 받아오시겠습니까?')) return;
    pollPausedRef.current = false;
    setLoading(true);
    try {
      const msgs = await fullReloadMessages(10000);
      setAllMessages(msgs);
    } finally { setLoading(false); }
  }

  // ─── Filtering ───
  const filtered = useMemo(() => {
    let result = allMessages;
    if (excludeBots) result = result.filter(m => !m.isBot);
    if (excludeNotifications) result = result.filter(m => !m.isNearbyNotification);
    if (filterMode === 'users') result = result.filter(m => !m.isBot);
    if (filterMode === 'bots') result = result.filter(m => m.isBot);
    if (search) {
      const kw = search.toLowerCase();
      result = result.filter(m =>
        m.text?.toLowerCase().includes(kw) ||
        m.senderNickname?.toLowerCase().includes(kw) ||
        m.senderId?.toLowerCase().includes(kw)
      );
    }
    return result;
  }, [allMessages, search, filterMode, excludeBots, excludeNotifications]);

  // ─── Pagination ───
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  totalPagesRef.current = totalPages;
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedMessages = useMemo(() => {
    const start = (safeCurrentPage - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, safeCurrentPage, perPage]);

  useEffect(() => { if (currentPage > totalPages) setCurrentPage(totalPages); }, [totalPages]);

  // ─── Message interactions ───
  function handleMessageClick(msg: PublicMessage) {
    setSelectedMsg(msg);
    const minM = parseInt(minDistance) || 1500;
    const maxM = parseInt(maxDistance) || 5000;
    const nearby = generateRandomNearby(msg.lat, msg.lng, minM, maxM);
    setSenderLat(nearby.lat.toFixed(6));
    setSenderLng(nearby.lng.toFixed(6));
    const dist = calcDistance(msg.lat, msg.lng, nearby.lat, nearby.lng);
    setAppliedDistance(`${Math.round(dist)}m (발신자: ${msg.senderNickname})`);
  }

  // ─── Virtual sender ───
  async function handleFindUser() {
    if (!senderNickname) return;
    const results = await searchUsersByNickname(senderNickname);
    if (results.length === 0) {
      alert(`"${senderNickname}"을(를) 포함하는 닉네임의 사용자를 찾을 수 없습니다.`);
      setShowNicknameDropdown(false);
    } else if (results.length === 1) {
      applyUser(results[0]);
    } else {
      setNicknameResults(results);
      setShowNicknameDropdown(true);
    }
  }

  function applyUser(user: User) {
    setSenderId(user.uid);
    setSenderEmail(user.email);
    setSenderNickname(user.nickname);
    setSenderGender(user.gender || 'unknown');
    if (user.lat != null && user.lng != null) {
      setSenderLat(user.lat.toFixed(6));
      setSenderLng(user.lng.toFixed(6));
    }
    setShowNicknameDropdown(false);
  }

  async function handleApplyByEmail() {
    if (!senderEmail) return;
    const user = await findUserByEmail(senderEmail);
    if (user) applyUser(user);
    else alert(`"${senderEmail}" 이메일의 사용자를 찾을 수 없습니다.`);
  }

  async function handleSend() {
    if (!sendText.trim() || !senderId) {
      alert('메시지와 발신자를 설정해주세요.');
      return;
    }
    await sendMessage({
      text: sendText, senderId, senderNickname: senderNickname || 'Unknown',
      gender: senderGender,
      lat: parseFloat(senderLat) || 0, lng: parseFloat(senderLng) || 0,
      radius: 1000,
    });
    setSendText('');
    // Refresh to see new message, then jump to latest
    setTimeout(async () => {
      await handleFetchRecent();
      goToLatest();
    }, 500);
  }

  // ─── Selection ───
  const lastSelectedMsgRef = useRef<string | null>(null);

  function toggleSelect(id: string, e: React.MouseEvent) {
    e.stopPropagation();
    const s = new Set(selected);
    if (e.shiftKey && lastSelectedMsgRef.current) {
      const ids = paginatedMessages.map(m => m.id);
      const lastIdx = ids.indexOf(lastSelectedMsgRef.current);
      const curIdx = ids.indexOf(id);
      if (lastIdx >= 0 && curIdx >= 0) {
        const [start, end] = lastIdx < curIdx ? [lastIdx, curIdx] : [curIdx, lastIdx];
        for (let i = start; i <= end; i++) s.add(ids[i]);
      }
    } else if (e.ctrlKey || e.metaKey) {
      s.has(id) ? s.delete(id) : s.add(id);
    } else {
      s.has(id) ? s.delete(id) : s.add(id);
    }
    lastSelectedMsgRef.current = id;
    setSelected(s);
  }

  function toggleSelectAll() {
    if (selected.size === paginatedMessages.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(paginatedMessages.map(m => m.id)));
    }
  }

  async function handleDeleteSelected() {
    if (!selected.size) return;
    if (!confirm(`${selected.size}개 메시지를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) return;
    await deleteMultipleMessages([...selected]);
    setSelected(new Set());
    setAllMessages(getCachedMessages());
  }

  // ─── Frequency distance calc ───
  function handleCalcFreqDistance() {
    const c1 = frequencyToCoords(freq1);
    const c2 = frequencyToCoords(freq2);
    if (c1 && c2) setFreqDistance(formatDistance(calcDistance(c1.lat, c1.lng, c2.lat, c2.lng)));
    else setFreqDistance('주파수 형식 오류');
  }

  function setFreq1FromMessage() { if (selectedMsg) setFreq1(coordsToFrequency(selectedMsg.lat, selectedMsg.lng)); }
  function setFreq2FromMessage() { if (selectedMsg) setFreq2(coordsToFrequency(selectedMsg.lat, selectedMsg.lng)); }

  function exportMessages(format: 'csv' | 'json') {
    const data = filtered.map(m => ({
      id: m.id, sender: m.senderNickname, senderId: m.senderId, gender: m.gender,
      text: m.text, lat: m.lat, lng: m.lng,
      frequency: coordsToFrequency(m.lat, m.lng),
      radius: m.radius, isBot: m.isBot, time: formatTimestamp(m.timestamp),
    }));
    if (format === 'csv') downloadCSV(data, 'messages.csv');
    else downloadJSON(data, 'messages.json');
  }

  const senderFreq = senderLat && senderLng ? coordsToFrequency(parseFloat(senderLat), parseFloat(senderLng)) : 'N/A';
  const freqParts = senderFreq !== 'N/A' ? senderFreq.split('-') : ['', '', '', ''];
  const cacheTs = getCacheTimestamp();

  return (
    <div className="flex flex-col" style={{ minHeight: '100%' }}>
      {/* Top bar */}
      <div className="flex items-center gap-2 mb-2 flex-wrap text-xs sm:text-sm">
        <h1 className="text-base sm:text-lg font-bold mr-2">채팅 내역</h1>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="검색..." className="w-40 px-2 py-1 border border-gray-300 rounded text-sm" />
        <select value={filterMode} onChange={e => setFilterMode(e.target.value as FilterMode)} className="px-2 py-1 border border-gray-300 rounded text-sm">
          <option value="all">전체 사용자</option>
          <option value="users">사용자만</option>
          <option value="bots">봇만</option>
        </select>
        <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={excludeBots} onChange={e => setExcludeBots(e.target.checked)} />Bot 제외</label>
        <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={excludeNotifications} onChange={e => setExcludeNotifications(e.target.checked)} />접속알림 제외</label>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500">선택: {selected.size}개</span>
          <span className="text-xs text-gray-400">검색결과: {filtered.length}개 / 전체: {allMessages.length}개</span>
          <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />실시간</label>
          <span className="text-xs text-gray-400">페이지당:</span>
          <select value={perPage} onChange={e => handlePerPageChange(Number(e.target.value))} className="w-16 px-1 py-1 border border-gray-300 rounded text-xs">
            {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      {/* Fetch controls */}
      <div className="flex items-center gap-1 mb-1 flex-wrap">
        <span className="text-xs text-gray-400">메시지 수신:</span>
        <button onClick={() => handleFetchPeriod(1)} disabled={loading} className="px-2 py-0.5 text-xs bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-50">최근 24시간</button>
        <button onClick={() => handleFetchPeriod(7)} disabled={loading} className="px-2 py-0.5 text-xs bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-50">최근 7일</button>
        <button onClick={() => handleFetchPeriod(30)} disabled={loading} className="px-2 py-0.5 text-xs bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-50">최근 30일</button>
        <button onClick={() => setShowDateRange(!showDateRange)} className="px-2 py-0.5 text-xs bg-gray-50 border border-gray-200 rounded hover:bg-gray-100">기간 지정</button>
        <button onClick={handleFetchRecent} disabled={loading} className="px-2 py-0.5 text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50">
          {loading ? '수신 중...' : '최신 수신'}
        </button>
        <button onClick={handleFullReload} disabled={loading} className="px-2 py-0.5 text-xs bg-orange-50 text-orange-600 border border-orange-200 rounded hover:bg-orange-100 disabled:opacity-50">전체 다시 받기</button>
        {cacheTs > 0 && <span className="text-[10px] text-gray-400 ml-1">캐시: {new Date(cacheTs).toLocaleString('ko-KR')}</span>}
      </div>

      {/* Date range picker */}
      {showDateRange && (
        <div className="flex items-center gap-2 mb-1 p-2 bg-gray-50 rounded-lg">
          <label className="text-xs text-gray-500">시작일:</label>
          <input type="date" value={dateStart} onChange={e => setDateStart(e.target.value)} className="px-2 py-0.5 border border-gray-300 rounded text-xs" />
          <label className="text-xs text-gray-500">종료일:</label>
          <input type="date" value={dateEnd} onChange={e => setDateEnd(e.target.value)} className="px-2 py-0.5 border border-gray-300 rounded text-xs" />
          <button onClick={handleFetchCustomRange} disabled={loading || !dateStart || !dateEnd} className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded disabled:opacity-50">수신</button>
          <button onClick={() => setShowDateRange(false)} className="px-2 py-0.5 text-xs text-gray-500 border border-gray-300 rounded">닫기</button>
        </div>
      )}

      <div className="flex items-center gap-2 mb-1">
        <p className="text-xs text-gray-400">(체크박스 또는 Ctrl+클릭: 개별 선택, Shift+클릭: 범위 선택, 더블클릭: 상세정보)</p>
        {selected.size > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs font-medium text-blue-700">{selected.size}개 선택</span>
            <button onClick={handleDeleteSelected} className="px-2 py-0.5 text-xs bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100">선택 삭제</button>
            <button onClick={() => setSelected(new Set())} className="px-2 py-0.5 text-xs text-gray-500 border border-gray-300 rounded hover:bg-gray-100">선택 해제</button>
          </div>
        )}
      </div>

      {/* ─── Pagination top ─── */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1">
          <button onClick={() => setCurrentPage(1)} disabled={safeCurrentPage <= 1} className="px-1.5 py-0.5 text-xs border border-gray-300 rounded disabled:opacity-30">&laquo;</button>
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safeCurrentPage <= 1} className="px-1.5 py-0.5 text-xs border border-gray-300 rounded disabled:opacity-30">&lsaquo;</button>
          <span className="text-xs text-gray-600 mx-1">
            페이지 {safeCurrentPage} / {totalPages} ({filtered.length}건)
          </span>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safeCurrentPage >= totalPages} className="px-1.5 py-0.5 text-xs border border-gray-300 rounded disabled:opacity-30">&rsaquo;</button>
          <button onClick={() => setCurrentPage(totalPages)} disabled={safeCurrentPage >= totalPages} className="px-1.5 py-0.5 text-xs border border-gray-300 rounded disabled:opacity-30">&raquo;</button>
          <input
            type="number"
            min={1}
            max={totalPages}
            className="w-12 px-1 py-0.5 text-xs border border-gray-300 rounded text-center ml-1"
            placeholder="#"
            onKeyDown={e => { if (e.key === 'Enter') { const v = parseInt((e.target as HTMLInputElement).value); if (v >= 1 && v <= totalPages) setCurrentPage(v); }}}
          />
        </div>
      </div>

      {/* ─── Message table ─── */}
      <div className="flex-1 overflow-auto bg-white rounded-lg border border-gray-200 text-sm" style={{ minHeight: '300px' }} tabIndex={0} onKeyDown={e => e.key === 'Escape' && setSelected(new Set())}>
        <table className="w-full min-w-[800px]">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr className="text-xs text-gray-500 uppercase whitespace-nowrap">
              <th className="px-1 py-2 w-8">
                <input type="checkbox" checked={paginatedMessages.length > 0 && selected.size === paginatedMessages.length} onChange={toggleSelectAll} />
              </th>
              <th className="px-2 py-2 text-left w-16">성별</th>
              <th className="px-2 py-2 text-left w-32">닉네임</th>
              <th className="px-2 py-2 text-left w-40">주파수</th>
              <th className="px-2 py-2 text-left">메세지</th>
              <th className="px-2 py-2 text-right w-40">발신시간</th>
            </tr>
          </thead>
          <tbody>
            {paginatedMessages.map(msg => {
              const isSelected = selected.has(msg.id);
              const isHighlighted = selectedMsg?.id === msg.id;
              const freq = coordsToFrequency(msg.lat, msg.lng);
              return (
                <tr
                  key={msg.id}
                  className={`cursor-pointer border-b border-gray-100 ${isHighlighted ? 'bg-blue-600 text-white' : isSelected ? 'bg-blue-100' : 'hover:bg-gray-50'}`}
                  onClick={() => handleMessageClick(msg)}
                  onDoubleClick={() => setDetailMsg(msg)}
                >
                  <td className="px-1 py-1" onClick={e => toggleSelect(msg.id, e)}>
                    <input type="checkbox" checked={isSelected} readOnly />
                  </td>
                  <td className={`px-2 py-1 ${msg.gender === 'female' ? (isHighlighted ? 'text-pink-200' : 'text-pink-600') : msg.gender === 'male' ? (isHighlighted ? 'text-blue-200' : 'text-blue-600') : (isHighlighted ? 'text-gray-300' : 'text-gray-500')}`}>
                    {msg.gender}
                  </td>
                  <td className="px-2 py-1">
                    <button className={`hover:underline font-medium ${isHighlighted ? 'text-yellow-200' : msg.isBot ? 'text-gray-400' : ''}`}
                      onClick={(e) => { e.stopPropagation(); setShowUserModal(msg.senderId); }}>
                      {msg.senderNickname}
                    </button>
                  </td>
                  <td className={`px-2 py-1 font-mono text-xs whitespace-nowrap ${isHighlighted ? 'text-gray-200' : 'text-gray-500'}`}>{freq !== 'N/A' ? freq : 'N/A'}</td>
                  <td className="px-2 py-1 truncate max-w-md">{msg.text}</td>
                  <td className={`px-2 py-1 text-right text-xs whitespace-nowrap ${isHighlighted ? 'text-gray-200' : 'text-gray-400'}`}>{formatTimestamp(msg.timestamp)}</td>
                </tr>
              );
            })}
            {paginatedMessages.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">{loading ? '로딩 중...' : '메시지 없음'}</td></tr>
            )}
            <tr ref={tableEndRef}><td /></tr>
          </tbody>
        </table>
      </div>

      {/* ─── Pagination bottom ─── */}
      <div className="flex items-center justify-center gap-1 mt-1">
        <button onClick={() => setCurrentPage(1)} disabled={safeCurrentPage <= 1} className="px-1.5 py-0.5 text-xs border border-gray-300 rounded disabled:opacity-30">&laquo;</button>
        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safeCurrentPage <= 1} className="px-1.5 py-0.5 text-xs border border-gray-300 rounded disabled:opacity-30">&lsaquo;</button>
        <span className="text-xs text-gray-600 mx-2">페이지 {safeCurrentPage} / {totalPages}</span>
        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safeCurrentPage >= totalPages} className="px-1.5 py-0.5 text-xs border border-gray-300 rounded disabled:opacity-30">&rsaquo;</button>
        <button onClick={() => setCurrentPage(totalPages)} disabled={safeCurrentPage >= totalPages} className="px-1.5 py-0.5 text-xs border border-gray-300 rounded disabled:opacity-30">&raquo;</button>
      </div>

      {/* ─── Bottom panel - 3 columns ─── */}
      <div className="mt-2 grid grid-cols-1 lg:grid-cols-3 gap-3 whitespace-nowrap">
        {/* Left: Sender info */}
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <h3 className="text-sm font-bold text-blue-700 mb-2">사용자 정보 및 위치</h3>
          <div className="space-y-1.5 text-sm">
            <UserFinder
              onUserSelect={applyUser}
              showSelectedInfo={false}
              nickname={senderNickname}
              email={senderEmail}
            />
            <div className="flex items-center gap-1">
              <label className="w-14 text-gray-500">성별:</label>
              <select value={senderGender} onChange={e => setSenderGender(e.target.value as 'male' | 'female' | 'unknown')} className="px-2 py-0.5 border border-gray-300 rounded text-sm">
                <option value="male">male</option><option value="female">female</option><option value="unknown">unknown</option>
              </select>
              <span className="text-xs text-gray-400 ml-1 truncate">senderId: {senderId ? senderId.substring(0, 16) + '...' : '-'}</span>
            </div>
            <div className="flex items-center gap-1">
              <label className="w-14 text-gray-500">위도:</label>
              <input value={senderLat} onChange={e => setSenderLat(e.target.value)} className="flex-1 px-2 py-0.5 border border-gray-300 rounded text-sm font-mono" />
            </div>
            <div className="flex items-center gap-1">
              <label className="w-14 text-gray-500">경도:</label>
              <input value={senderLng} onChange={e => setSenderLng(e.target.value)} className="flex-1 px-2 py-0.5 border border-gray-300 rounded text-sm font-mono" />
            </div>
            <div className="flex items-center gap-1">
              <label className="w-14 text-gray-500">주파수:</label>
              <div className="flex gap-1 font-mono text-sm">
                {freqParts.map((p, i) => <span key={i} className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded">{p || '---'}</span>)}
              </div>
            </div>
            {appliedDistance && <p className="text-xs text-red-600 mt-1">적용된 거리: {appliedDistance}</p>}
          </div>
        </div>

        {/* Center: Message send + freq calc */}
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <h3 className="text-sm font-bold text-blue-700 mb-2">메시지 전송</h3>
          <div className="space-y-2">
            <div>
              <label className="text-xs text-gray-500">메시지:</label>
              <textarea value={sendText} onChange={e => setSendText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }}}
                rows={2} className="w-full px-2 py-1 border border-gray-300 rounded text-sm resize-none" />
            </div>
            <div className="flex gap-2">
              <button onClick={handleSend} className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">메시지 전송</button>
              <button onClick={handleDeleteSelected} disabled={!selected.size} className="px-3 py-1 text-sm bg-red-50 text-red-600 border border-red-200 rounded disabled:opacity-40">선택된 채팅 삭제</button>
            </div>
            <div className="border-t border-gray-200 pt-2 mt-2">
              <h4 className="text-xs font-bold text-gray-600 mb-1">주파수 간 거리 계산:</h4>
              <div className="flex items-center gap-1 text-xs mb-1">
                <span className="text-gray-500">주파수 1:</span>
                <input value={freq1} onChange={e => setFreq1(e.target.value)} className="w-36 px-1 py-0.5 border border-gray-300 rounded font-mono text-xs" />
                <button onClick={setFreq1FromMessage} className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-[10px]">메시지에서</button>
              </div>
              <div className="flex items-center gap-1 text-xs mb-1">
                <span className="text-gray-500">주파수 2:</span>
                <input value={freq2} onChange={e => setFreq2(e.target.value)} className="w-36 px-1 py-0.5 border border-gray-300 rounded font-mono text-xs" />
                <button onClick={setFreq2FromMessage} className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-[10px]">메시지에서</button>
                {freqDistance && <span className="ml-2 font-bold text-blue-600">거리: {freqDistance}</span>}
              </div>
              <button onClick={handleCalcFreqDistance} className="px-2 py-0.5 text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded">계산</button>
            </div>
          </div>
        </div>

        {/* Right: Random location + export */}
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <h3 className="text-sm font-bold text-blue-700 mb-2">메시지 클릭시 랜덤 위치 설정:</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <label className="text-gray-500">최소 거리(m):</label>
              <input value={minDistance} onChange={e => setMinDistance(e.target.value)} className="w-24 px-2 py-0.5 border border-gray-300 rounded text-sm" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-gray-500">최대 거리(m):</label>
              <input value={maxDistance} onChange={e => setMaxDistance(e.target.value)} className="w-24 px-2 py-0.5 border border-gray-300 rounded text-sm" />
            </div>
          </div>
          <div className="border-t border-gray-200 pt-2 mt-3">
            <h4 className="text-xs font-bold text-gray-600 mb-2">내보내기 / 일괄 작업</h4>
            <div className="flex flex-wrap gap-1">
              <button onClick={() => exportMessages('csv')} className="px-2 py-1 text-xs bg-gray-50 border border-gray-200 rounded">CSV</button>
              <button onClick={() => exportMessages('json')} className="px-2 py-1 text-xs bg-gray-50 border border-gray-200 rounded">JSON</button>
              <button onClick={async () => { if (confirm('봇 메시지 전체 삭제?')) { const n = await deleteAllBotMessages(); alert(`${n}개 삭제`); setAllMessages(getCachedMessages()); }}} className="px-2 py-1 text-xs bg-orange-50 text-orange-600 border border-orange-200 rounded">봇 삭제</button>
              <button onClick={async () => { if (confirm('전체 메시지 삭제?')) { const n = await deleteAllMessages(); alert(`${n}개 삭제`); setAllMessages([]); }}} className="px-2 py-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded">전체 삭제</button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {detailMsg && <MessageDetailModal message={detailMsg} onClose={() => setDetailMsg(null)} />}
      {showUserModal && <UserDetailModal uid={showUserModal} onClose={() => setShowUserModal(null)} />}
    </div>
  );
}
